import { CEFR_LEVEL_ORDER, getReadingMinutes } from "./storiesHelpers";

export const FALLBACK_STORY_EMOJI = "📖";

export const STORY_SOURCES = [
  { id: "stories", label: "Stories", rowLabel: "Story", rowIcon: "book" },
  { id: "wikipedia", label: "Wikipedia articles", rowLabel: "Wikipedia", rowIcon: "newspaper-o" },
  { id: "mine", label: "My cloze-texts", rowLabel: "My cloze-text", rowIcon: "pencil" }
];

export const STORY_STATUSES = [
  { id: "not_started", label: "Not started" },
  { id: "in_progress", label: "In progress" },
  { id: "complete", label: "Complete" }
];

export const STORY_LEVELS = CEFR_LEVEL_ORDER.map((cefrLevel) => ({ id: cefrLevel, label: cefrLevel }));

const LEVEL_DOT_BY_LETTER = { A: "easy", B: "medium", C: "hard" };

export const STORY_TOPICS = [
  { id: "everyday", emoji: "☕", label: "Everyday life and mind", categories: ["everyday", "psychology", "anthropology, psychology and everyday life"] },
  { id: "nature", emoji: "🌿", label: "Nature and health", categories: ["nature", "health", "biology and health sciences"] },
  { id: "science", emoji: "🔬", label: "Science and maths", categories: ["science", "physical sciences", "mathematics"] },
  { id: "history", emoji: "🏛️", label: "History", categories: ["history"] },
  { id: "places", emoji: "🌍", label: "Places", categories: ["geography"] },
  { id: "technology", emoji: "💻", label: "Technology", categories: ["technology"] },
  { id: "people", emoji: "👤", label: "People", categories: ["people"] },
  { id: "arts", emoji: "🎨", label: "Arts", categories: ["arts"] },
  { id: "society", emoji: "🤝", label: "Society", categories: ["society and social sciences"] },
  { id: "beliefs", emoji: "💭", label: "Beliefs and big ideas", categories: ["philosophy and religion"] },
  { id: "food", emoji: "🍲", label: "Food", categories: ["food"] },
  { id: "books", emoji: "📚", label: "Books", categories: ["nonfiction"] }
];

export const STORY_SORTS = [
  { id: "recently_opened", label: "Recently opened" },
  { id: "easiest", label: "Easiest first" },
  { id: "shortest", label: "Shortest first" },
  { id: "alphabetical", label: "A to Z" }
];

export const DEFAULT_STORY_SORT = "recently_opened";

export const DEFAULT_STORY_FILTERS = {
  sources: ["stories", "wikipedia", "mine"],
  statuses: [],
  levels: [],
  topics: []
};

export const pluralizeTexts = (count) => `${count} ${count === 1 ? "text" : "texts"}`;

export const getStoryKey = (source, slug) => `${source}-${slug}`;

export const getStoryPath = (storiesPath, { slug, source }) => (source === "stories" ? `${storiesPath}/${slug}` : `${storiesPath}/${source}/${slug}`);

const findTopic = (category) =>
  STORY_TOPICS.find((topic) => !!category && topic.categories.includes(category.toLowerCase()))?.id ?? null;

export const getStoryLevel = (cefrLevel) => (CEFR_LEVEL_ORDER.includes(cefrLevel) ? cefrLevel : null);

export const getStoryLevelDot = (level) => LEVEL_DOT_BY_LETTER[level[0]];

const getLevelRank = (cefrLevel) => {
  const index = cefrLevel ? CEFR_LEVEL_ORDER.indexOf(cefrLevel) : -1;
  return index === -1 ? CEFR_LEVEL_ORDER.length : index;
};

export const compInputToStoryListItem = (compInput, openedTimes) => {
  const key = getStoryKey("stories", compInput.slug);
  return {
    emoji: compInput.emoji || FALLBACK_STORY_EMOJI,
    isFree: true,
    key,
    lastOpenedAt: openedTimes[key] ?? 0,
    level: getStoryLevel(compInput.cefrLevel),
    levelRank: getLevelRank(compInput.cefrLevel),
    minutes: getReadingMinutes(compInput.wordCount),
    slug: compInput.slug,
    source: "stories",
    status: compInput.status ?? "not_started",
    title: compInput.translatedTitle || compInput.title,
    topic: findTopic(compInput.category),
    wordCount: compInput.wordCount
  };
};

export const clozeTextToStoryListItem = (clozeText, openedTimes, source = "wikipedia") => {
  const key = getStoryKey(source, clozeText.slug);
  return {
    emoji: clozeText.emoji || FALLBACK_STORY_EMOJI,
    isFree: clozeText.isFree,
    isReady: !clozeText.processingStatus || clozeText.processingStatus === "complete",
    processingStatus: clozeText.processingStatus ?? null,
    key,
    lastOpenedAt: Math.max(openedTimes[key] ?? 0, (clozeText.progress.lastPlay ?? 0) * 1000),
    level: getStoryLevel(clozeText.cefrLevel),
    levelRank: getLevelRank(clozeText.cefrLevel),
    minutes: getReadingMinutes(clozeText.wordCount),
    slug: clozeText.slug,
    source,
    status: clozeText.progress.status,
    title: clozeText.title,
    topic: findTopic(clozeText.category),
    wordCount: clozeText.wordCount
  };
};

const matchesSelection = (selected, value) => !selected.length || (value !== null && selected.includes(value));

export const filterStories = (items, filters) =>
  items.filter(
    (item) =>
      filters.sources.includes(item.source) &&
      matchesSelection(filters.statuses, item.status) &&
      matchesSelection(filters.levels, item.level) &&
      matchesSelection(filters.topics, item.topic)
  );

const compareTitles = (a, b) => a.title.localeCompare(b.title);
const compareLevels = (a, b) => a.levelRank - b.levelRank || compareTitles(a, b);

const COMPARE_BY_SORT = {
  recently_opened: (a, b) => b.lastOpenedAt - a.lastOpenedAt || compareLevels(a, b),
  easiest: compareLevels,
  shortest: (a, b) => a.wordCount - b.wordCount || compareTitles(a, b),
  alphabetical: compareTitles
};

export const sortStories = (items, sort) => [...items].sort(COMPARE_BY_SORT[sort]);

export const getTopicsWithStories = (items) => STORY_TOPICS.filter((topic) => items.some((item) => item.topic === topic.id));

export const countActiveFilters = (filters) => {
  const sourcesChanged =
    filters.sources.length !== DEFAULT_STORY_FILTERS.sources.length ||
    filters.sources.some((source) => !DEFAULT_STORY_FILTERS.sources.includes(source));
  return filters.statuses.length + filters.levels.length + filters.topics.length + (sourcesChanged ? 1 : 0);
};

const isOnlySource = (sources, value) => sources.length === 1 && sources[0] === value;

const toggleSelection = (selected, value) =>
  selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value];

export const toggleFilter = (filters, group, value) => {
  if (group === "sources" && isOnlySource(filters.sources, value)) return filters;
  return { ...filters, [group]: toggleSelection(filters[group], value) };
};

export const CLOZE_READING_CHALLENGE_SENTENCES = 5000;

const summarizeCategory = (category, clozeTexts) => ({
  articleCount: clozeTexts.length,
  category,
  completedCount: clozeTexts.filter((clozeText) => clozeText.progress.status === "complete").length,
  playedCount: clozeTexts.filter((clozeText) => clozeText.progress.status !== "not_started").length,
  sentenceCount: clozeTexts.reduce((sum, clozeText) => sum + (clozeText.clozeCount ?? 0), 0)
});

export const getWikipediaProgress = (clozeTexts, sentenceCount) => {
  const clozeTextsByCategory = new Map();
  clozeTexts.forEach((clozeText) => {
    const category = clozeText.category || "Other";
    clozeTextsByCategory.set(category, [...(clozeTextsByCategory.get(category) ?? []), clozeText]);
  });
  return {
    categories: [...clozeTextsByCategory.keys()].sort().map((category) => summarizeCategory(category, clozeTextsByCategory.get(category))),
    sentenceCount: sentenceCount ?? 0
  };
};
